import React, { useCallback } from 'react';
import { Table, Button, Space, Tag, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useLearningCenterFaqArticleList } from '../hooks/useLearningCenterFaqConfigList';
import { DELETE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE, TOGGLE_DRIVER_LEARNING_CENTER_FAQ_NODE_ENABLED } from '../graphql/mutations';
import { DriverLearningCenterFaqNode } from '../graphql/types';
import { formatEpochToDateTime } from '../../../../utils/dateTimeUtils';
import { resolveLearningCenterErrorMessage } from '../utils/errorMessage';

interface FaqArticleSubTableProps {
  categoryId: string;
  keyword: string;
  isEnabled: boolean | null;
  onEdit: (article: DriverLearningCenterFaqNode) => void;
}

const FaqArticleSubTable: React.FC<FaqArticleSubTableProps> = ({ categoryId, keyword, isEnabled, onEdit }) => {
  const { t } = useTranslation();
  const { articles, loading, refetch } = useLearningCenterFaqArticleList(categoryId, keyword, isEnabled, false);

  const [deleteArticle] = useMutation(DELETE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE);
  const [toggleEnabled] = useMutation(TOGGLE_DRIVER_LEARNING_CENTER_FAQ_NODE_ENABLED);

  const handleToggle = useCallback(async (article: DriverLearningCenterFaqNode) => {
    try {
      await toggleEnabled({ variables: { id: article.id, isEnabled: !article.isEnabled } });
      message.success(t('learningCenterConfig.messages.toggleSuccess'));
      refetch();
    } catch (err: any) {
      const errorMessage = resolveLearningCenterErrorMessage(err, t);
      if (errorMessage) {
        message.error(errorMessage);
      }
    }
  }, [toggleEnabled, refetch, t]);

  const handleDelete = useCallback((article: DriverLearningCenterFaqNode) => {
    Modal.confirm({
      title: t('learningCenterConfig.messages.deleteArticleConfirmTitle'),
      content: t('learningCenterConfig.messages.deleteArticleConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteArticle({ variables: { id: article.id } });
          message.success(t('learningCenterConfig.messages.deleteSuccess'));
          refetch();
        } catch (err: any) {
          const errorMessage = resolveLearningCenterErrorMessage(err, t);
          if (errorMessage) {
            message.error(errorMessage);
          }
        }
      },
    });
  }, [deleteArticle, refetch, t]);

  const columns = [
    {
      title: t('learningCenterConfig.columns.question'),
      dataIndex: 'questionZh',
      key: 'questionZh',
      render: (value: string, record: DriverLearningCenterFaqNode) => value || record.questionEn || '-',
    },
    {
      title: t('learningCenterConfig.columns.sortOrder'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 90,
    },
    {
      title: t('learningCenterConfig.columns.status'),
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? t('learningCenterConfig.filter.enabled') : t('learningCenterConfig.filter.disabled')}
        </Tag>
      ),
    },
    {
      title: t('learningCenterConfig.columns.updatedBy'),
      dataIndex: 'updatedByName',
      key: 'updatedByName',
      width: 140,
      render: (value: string | null, record: DriverLearningCenterFaqNode) => value || record.updatedBy || '-',
    },
    {
      title: t('learningCenterConfig.columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: number | string | null) => formatEpochToDateTime(value),
    },
    {
      title: t('learningCenterConfig.columns.action'),
      key: 'action',
      width: 200,
      render: (_: any, record: DriverLearningCenterFaqNode) => (
        <Space>
          <Button type="link" onClick={() => onEdit(record)}>{t('learningCenterConfig.buttons.edit')}</Button>
          <Button type="link" onClick={() => handleToggle(record)}>
            {record.isEnabled ? t('learningCenterConfig.buttons.disable') : t('learningCenterConfig.buttons.enable')}
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>{t('learningCenterConfig.buttons.delete')}</Button>
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={articles}
      loading={loading}
      pagination={false}
      size="small"
    />
  );
};

export default FaqArticleSubTable;
